<template>
  <v-dialog
    v-model="dialog"
    max-width="480"
    persistent
  >
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon color="primary" class="mr-2">mdi-qrcode</v-icon>
        Receber {{ coin }}
        <v-spacer></v-spacer>
        <v-btn icon="mdi-close" variant="text" @click="dialog = false"></v-btn>
      </v-card-title>

      <v-card-text class="text-center">
        <v-sheet
          class="mx-auto mb-4 qr-container pa-4"
          elevation="1"
          rounded
        >
          <!-- QR Code será renderizado aqui -->
          <div ref="qrCodeContainer" class="qrcode-wrapper mx-auto"></div>
          
          <!-- Endereço da carteira -->
          <div class="wallet-address mt-4">
            <div class="text-caption mb-1">Seu endereço QuarnX:</div>
            <div class="d-flex align-center justify-center">
              <div class="address-text">{{ dexStore.wallet.address }}</div>
              <v-btn
                icon="mdi-content-copy"
                variant="text"
                density="comfortable"
                size="small"
                @click="copyAddressToClipboard"
                class="ml-1"
              ></v-btn>
            </div>
          </div>
        </v-sheet>

        <v-divider class="my-3"></v-divider>

        <!-- Opções adicionais -->
        <div class="options-container">
          <!-- Seleção de rede -->
          <v-select
            v-model="selectedNetwork"
            :items="networks"
            item-title="name"
            item-value="id"
            label="Rede"
            variant="outlined"
            density="compact"
            hide-details
            class="mb-3"
            return-object
          >
            <template v-slot:selection="{ item }">
              <div class="d-flex align-center">
                <v-avatar size="24" :color="item.raw.color" class="mr-2">
                  <span class="text-caption text-white">{{ item.raw.icon }}</span>
                </v-avatar>
                {{ item.raw.name }}
              </div>
            </template>
          </v-select>

          <!-- Campo de valor a receber (opcional) -->
          <v-text-field
            v-model="amount"
            label="Valor a receber (opcional)"
            hint="O valor será incluído no QR Code"
            persistent-hint
            variant="outlined"
            density="compact"
            type="number"
            min="0.000001"
            step="0.000001"
            class="mb-3"
            @input="updateQRCode"
          ></v-text-field>

          <!-- Campo de descrição (opcional) -->
          <v-text-field
            v-model="memo"
            label="Descrição (opcional)"
            hint="Será incluída no QR Code para referência"
            persistent-hint
            variant="outlined"
            density="compact"
            maxlength="100"
            counter
            class="mb-4"
            @input="updateQRCode"
          ></v-text-field>

          <!-- Botões de ação -->
          <div class="d-flex flex-wrap gap-2 justify-center">
            <v-btn
              variant="outlined"
              color="primary"
              prepend-icon="mdi-share-variant"
              @click="shareAddress"
              class="action-button"
            >
              Compartilhar
            </v-btn>
            <v-btn
              variant="outlined"
              color="primary"
              prepend-icon="mdi-download"
              @click="downloadQRCode"
              class="action-button"
            >
              Download QR
            </v-btn>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { useDexStore } from '@/stores/dex'
import { useAppStore } from '@/stores/app'
import QRCode from 'qrcode'

export default {
  name: 'ReceiveKinXDialog',
  
  props: {
    modelValue: {
      type: Boolean,
      default: false
    }
  },

  emits: ['update:modelValue'],
  
  data() {
    return {
      dialog: this.modelValue,
      selectedNetwork: null,
      amount: '',
      memo: '',
      networks: [
        { id: 'quarnx', name: 'QuarnX', color: 'primary', icon: 'Q' },
        { id: 'ethereum', name: 'Ethereum (Bridge)', color: '#6B7FFF', icon: 'E' },
        { id: 'bsc', name: 'BSC (Bridge)', color: '#F0B90B', icon: 'B' }
      ]
    }
  },
  
  computed: {
    dexStore() {
      return useDexStore()
    },
    appStore() {
      return useAppStore()
    },
    qrCodeData() {
      // Formatar dados para o QR code (endereço, valor, memo)
      let data = `quarnx:${this.dexStore.wallet.address}`
      
      // Adicionar parâmetros se especificados
      const params = []
      if (this.amount) params.push(`amount=${this.amount}`)
      if (this.memo) params.push(`memo=${encodeURIComponent(this.memo)}`)
      
      if (params.length > 0) {
        data += `?${params.join('&')}`
      }
      
      return data
    }
  },
  
  watch: {
    modelValue(val) {
      this.dialog = val
    },
    dialog(val) {
      if (!val) this.resetForm()
      this.$emit('update:modelValue', val)
      
      if (val) {
        // Renderizar QR code quando o diálogo é aberto
        this.$nextTick(() => {
          this.renderQRCode()
        })
      }
    }
  },
  
  methods: {
    renderQRCode() {
      if (!this.$refs.qrCodeContainer) return
      
      this.$refs.qrCodeContainer.innerHTML = ''
      
      // Usando toString em vez de toCanvas para melhor compatibilidade
      QRCode.toString(this.qrCodeData, {
        type: 'svg',
        width: 240,
        margin: 1,
        color: {
          dark: '#000000',
          light: '#ffffff'
        },
        errorCorrectionLevel: 'H'
      }, (error, svgString) => {
        if (error) {
          console.error('Erro ao gerar QR code:', error)
          this.appStore.showSnackbar({
            text: 'Erro ao gerar QR code',
            color: 'error'
          })
        } else {
          // Inserir o SVG no container
          this.$refs.qrCodeContainer.innerHTML = svgString
        }
      })
    },
    
    updateQRCode() {
      this.$nextTick(() => {
        this.renderQRCode()
      })
    },
    
    copyAddressToClipboard() {
      navigator.clipboard.writeText(this.dexStore.wallet.address).then(() => {
        this.appStore.showSnackbar({
          text: 'Endereço copiado para a área de transferência',
          color: 'success'
        })
      })
    },
    
    shareAddress() {
      // Verifica se a API Web Share está disponível
      if (navigator.share) {
        navigator.share({
          title: 'Meu endereço QuarnX',
          text: `Meu endereço para receber ${window.__QUARNX_COIN__ || 'tKinX'}: ${this.dexStore.wallet.address}`,
          url: `quarnx:${this.dexStore.wallet.address}`
        }).catch((error) => {
          console.error('Erro ao compartilhar:', error)
        })
      } else {
        // Fallback se Web Share API não estiver disponível
        this.copyAddressToClipboard()
      }
    },
    
    downloadQRCode() {
      // Encontrar o canvas onde o QR code foi renderizado
      const canvas = this.$refs.qrCodeContainer.querySelector('canvas')
      if (!canvas) return
      
      // Criar um link temporário para download
      const link = document.createElement('a')
      link.download = `quarnx-${this.dexStore.wallet.address.substring(0, 8)}.png`
      link.href = canvas.toDataURL('image/png')
      
      // Simular um clique no link
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      this.appStore.showSnackbar({
        text: 'QR Code salvo com sucesso',
        color: 'success'
      })
    },
    
    resetForm() {
      this.amount = ''
      this.memo = ''
      this.selectedNetwork = this.networks[0]
    }
  },
  
  mounted() {
    // Definir a rede padrão como QuarnX
    this.selectedNetwork = this.networks[0]
  }
}
</script>

<style scoped>
.qr-container {
  background-color: white;
  border-radius: 8px;
}

.qrcode-wrapper {
  width: 240px;
  height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.wallet-address {
  max-width: 100%;
  overflow-wrap: break-word;
  word-wrap: break-word;
}

.address-text {
  font-family: monospace;
  font-size: 0.85rem;
  background-color: rgba(0, 0, 0, 0.04);
  padding: 6px;
  border-radius: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 80%;
}

.options-container {
  max-width: 360px;
  margin: 0 auto;
}

.gap-2 {
  gap: 8px;
}

.action-button {
  flex-grow: 1;
  min-width: 140px;
  max-width: 170px;
}
</style>
